/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Input,
  message,
  Page,
  Row,
  Select,
  Text,
  Title,
} from 'components';
import styled from 'styled-components';

import axios, { AxiosError } from 'axios';
import { RetsController } from '../../../../../api';
import Instructions from './Instructions';

type OfficesUpdatedCountRes = {
  updateOfficesFromFeed: number;
};

const TriggerOfficeUpdatesForUsers = () => {
  const [loading, setLoading] = useState(true);
  const [mlsTypes, setMlsTypes] = useState<string[]>([]);
  const [chosenMls, setChosenMls] = useState<string>();
  const [oids, setOids] = useState('');

  const API_URL = '/org/qa_page/office_update/';
  const defaultErrMsg = 'Could not trigger office updates for users';

  // #region util functions
  const validateParams = () => {
    if (!chosenMls && oids.trim() === '') {
      throw new Error("At least select a MLS, or OIDs can't be empty");
    }
  };

  const buildOidsArr = (oidStr: string) => {
    const oidArr = oidStr ? oidStr.split(',') : [];
    let trimmedArr;
    if (oidArr.length) {
      trimmedArr = oidArr.map(oid => oid.trim());
    }
    return trimmedArr;
  };

  const buildUrl = ({
    mlsType,
    oidsArr,
    runDate,
  }: {
    mlsType?: string;
    oidsArr?: string[];
    runDate?: Date;
  }) => {
    // url to build api request here
    const searchParams = new URLSearchParams({
      mlsType,
      ...(runDate ? { runDate: runDate.toISOString() } : {}),
    });
    if (oidsArr && oidsArr.length) {
      for (const id of oidsArr) {
        searchParams.append('oids[]', id);
      }
    }
    return `${API_URL}?${searchParams.toString()}`;
  };

  const showError = (e: Error | AxiosError) => {
    if (axios.isAxiosError(e)) {
      message.error(e.response.data.message || defaultErrMsg);
    } else {
      message.error(e.message || defaultErrMsg);
    }
  };

  const clear = () => {
    setChosenMls('');
    setOids('');
  };
  // #endregion

  const triggerProcessAll = async () => {
    try {
      validateParams();
      const oidsArr = buildOidsArr(oids);
      const url = buildUrl({
        mlsType: chosenMls,
        oidsArr,
      });
      setLoading(true);
      const {
        data: { updateOfficesFromFeed: count },
      } = await axios.post<OfficesUpdatedCountRes>(url);
      message.success(`Updated ${count} org(s) data`);
      setLoading(false);
    } catch (e) {
      showError(e);
      setLoading(false);
    }
  };

  const triggerProcessPastHour = async () => {
    try {
      validateParams();
      const runDate = new Date();
      runDate.setHours(runDate.getHours() - 1);
      const oidsArr = buildOidsArr(oids);
      const url = buildUrl({
        mlsType: chosenMls,
        oidsArr,
        runDate,
      });
      setLoading(true);
      const {
        data: { updateOfficesFromFeed: count },
      } = await axios.post<OfficesUpdatedCountRes>(url);
      message.success(`Updated ${count} org(s) data from the past hour`);
      setLoading(false);
    } catch (e) {
      showError(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchMlsTypes = async () => {
      try {
        const res = await RetsController.getAllMlsTypes();
        setLoading(false);
        setMlsTypes(res);
      } catch (e) {
        showError(e);
      }
    };
    fetchMlsTypes();
  }, []);

  return (
    <Page type="filled" fillScreenHeight>
      <Layout>
        <Title level={1} hideMargin data-cy="page-title">
          Trigger cron to update the office info for the users
        </Title>
        <Text>
          This QA page is used to help trigger the update of office info for
          users
        </Text>
        <Instructions />
        <StyledForm>
          <Row gutter={16}>
            <Col span={24}>
              <Select
                label="Mls Type"
                disabled={loading}
                options={mlsTypes.map(value => ({ label: value, value }))}
                onSelect={setChosenMls}
                placeholder="Mls Type"
                showSearch
                value={chosenMls}
                dataCy="Mls-select"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Input
                label="Office OIDs"
                value={oids}
                onChange={e => {
                  setOids(e.target.value);
                }}
                disabled={loading}
                placeholder="oids of offices to target the cron, please split them by comma and remove spaces"
                dataCy="oid-input"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <StyledCol span={24}>
              <Button disabled={loading} onClick={triggerProcessAll}>
                Process
              </Button>
              <Button disabled={loading} onClick={triggerProcessPastHour}>
                Process From Last Hour
              </Button>
              <Button onClick={clear}>Clear Form</Button>
            </StyledCol>
          </Row>
        </StyledForm>
      </Layout>
    </Page>
  );
};

export default TriggerOfficeUpdatesForUsers;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 32px;
  padding: 20px;
  background: white;
`;

const StyledForm = styled.form`
  width: 750px;
  display: grid;
  grid-gap: 15px;
`;

const StyledCol = styled(Col)`
  display: flex;
  justify-content: space-between;
`;
